import React, { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import { Fingerprint, Loader2 } from 'lucide-react';
import { supabase } from '../../services/supabaseClient';
import { biometrie, biometrieStand, biometrieVerfuegbar } from '../../services/biometrie';
import {
    anmeldeFehlerArt, biometrieEinrichtenFehlerText, biometrieFehlerAus, biometrieKartenLage, type BiometrieStand,
} from '../../services/biometrieLogik';

/**
 * Anmeldung mit Fingerabdruck oder Gesicht für DIESES Gerät (App-Info, nur in
 * der App; Native-Plugin `Biometrie`, docs/android-app-vertrag.md).
 *
 * Einrichten: Passwort eingeben → wird gegen den Server geprüft (Anmeldung
 * desselben Kontos, App.tsx lädt dabei nichts neu) → Android fragt Finger
 * oder Gesicht ab → Passwort liegt AES-verschlüsselt in den privaten
 * Einstellungen der App, der Schlüssel im Android-Keystore (verlässt das
 * Gerät nie). Beim Anmelden geht das Passwort wie beim Eintippen an den Server.
 * Ausschalten löscht den Eintrag sofort. Die Anmeldemaske bietet danach
 * „Mit Fingerabdruck / Gesicht anmelden“ an.
 */

const KNOPF = 'inline-flex items-center justify-center gap-2 rounded-xl text-sm font-semibold px-4 py-3 min-h-[44px] disabled:opacity-50';

const Hinweis: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <p className="text-xs rounded-lg px-2.5 py-2 bg-amber-50 text-amber-900 border border-amber-200 leading-relaxed">{children}</p>
);

export function BiometrieKarte(): JSX.Element {
    const [pluginDa] = useState<boolean>(biometrieVerfuegbar);
    const [stand, setStand] = useState<BiometrieStand | null>(null);
    const [geladen, setGeladen] = useState(false);
    const [eigeneEmail, setEigeneEmail] = useState<string | null>(null);
    const [passwort, setPasswort] = useState('');
    const [beschaeftigt, setBeschaeftigt] = useState(false);
    const [fehler, setFehler] = useState('');
    const [erfolg, setErfolg] = useState('');

    const neuLaden = useCallback(async () => {
        setStand(await biometrieStand());
        setGeladen(true);
    }, []);

    useEffect(() => {
        if (!pluginDa) return;
        void neuLaden();
        supabase.auth.getSession()
            .then(({ data }) => setEigeneEmail(data.session?.user?.email ?? null))
            .catch(() => setEigeneEmail(null));
    }, [pluginDa, neuLaden]);

    const lage = biometrieKartenLage(pluginDa, stand, eigeneEmail);

    const einrichten = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!eigeneEmail || !passwort) return;
        // Das Passwort bleibt nicht im State/Feld stehen — egal, wie es ausgeht (auch bei Abbruch).
        const eingabe = passwort;
        setPasswort('');
        setBeschaeftigt(true);
        setFehler('');
        setErfolg('');
        try {
            // Erst prüfen, dann speichern — sonst läge ein Tippfehler in der Ablage.
            const { error } = await supabase.auth.signInWithPassword({ email: eigeneEmail, password: eingabe });
            if (error) {
                const art = anmeldeFehlerArt(error);
                setFehler(art === 'falsche_daten' ? 'Das Passwort stimmt nicht.'
                    : art === 'gesperrt' ? 'Dieses Konto ist gesperrt. Bitte an die Benutzerverwaltung wenden.'
                    : `Passwort konnte nicht geprüft werden (${error.message}). Bitte erneut versuchen.`);
                return;
            }
            try {
                await biometrie().speichern({ email: eigeneEmail, passwort: eingabe });
                setErfolg('Eingerichtet. Beim nächsten Anmelden genügt Finger oder Gesicht.');
            } catch (err) {
                const text = biometrieEinrichtenFehlerText(biometrieFehlerAus(err));
                if (text) setFehler(text);
            }
        } finally {
            setBeschaeftigt(false);
            await neuLaden();
        }
    };

    const ausschalten = async () => {
        setBeschaeftigt(true);
        setFehler('');
        setErfolg('');
        try {
            await biometrie().loeschen();
            setErfolg('Ausgeschaltet. Die gespeicherte Anmeldung ist gelöscht.');
        } catch {
            setFehler('Ausschalten nicht möglich — bitte erneut versuchen.');
        } finally {
            setBeschaeftigt(false);
            await neuLaden();
        }
    };

    const einrichtenFormular = (
        <form onSubmit={einrichten} className="space-y-2">
            <label className="block text-xs font-semibold text-slate-700" htmlFor="biometrie-passwort">
                Passwort für {eigeneEmail ?? 'Ihr Konto'}
            </label>
            <input
                id="biometrie-passwort"
                type="password"
                autoComplete="current-password"
                value={passwort}
                onChange={(e) => setPasswort(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-3 text-sm focus:outline-none focus:border-sky-500"
                placeholder="••••••••"
            />
            <button type="submit" disabled={beschaeftigt || !passwort || !eigeneEmail} className={`${KNOPF} w-full bg-sky-600 text-white active:bg-sky-700`}>
                {beschaeftigt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4" />} Einrichten
            </button>
        </form>
    );

    let inhalt: React.ReactNode;
    if (lage === 'update_noetig') {
        inhalt = <Hinweis>Diese App-Version kennt die Anmeldung mit Fingerabdruck/Gesicht noch nicht — bitte aktualisieren (ab 1.0.3).</Hinweis>;
    } else if (!geladen) {
        inhalt = <p className="text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Prüfe …</p>;
    } else if (lage === 'keine_hardware') {
        inhalt = <Hinweis>Dieses Gerät hat weder Fingerabdrucksensor noch Gesichtserkennung.</Hinweis>;
    } else if (lage === 'android_einrichten' || lage === 'nicht_verfuegbar') {
        inhalt = (
            <div className="space-y-2">
                <Hinweis>
                    {lage === 'android_einrichten'
                        ? 'In Android ist noch kein Fingerabdruck und kein Gesicht hinterlegt (Einstellungen → Sicherheit). Danach hier einrichten.'
                        : 'Fingerabdruck/Gesicht ist gerade nicht verfügbar.'}
                </Hinweis>
                <button type="button" onClick={() => void neuLaden()} className={`${KNOPF} border border-slate-300 text-slate-700 bg-white active:bg-slate-50`}>
                    Erneut prüfen
                </button>
            </div>
        );
    } else if (lage === 'an') {
        inhalt = (
            <div className="space-y-2">
                <p className="text-sm text-slate-700">Eingerichtet für <strong>{stand?.email}</strong>.</p>
                <button type="button" onClick={() => void ausschalten()} disabled={beschaeftigt} className={`${KNOPF} border border-slate-300 text-slate-700 bg-white active:bg-slate-50`}>
                    Ausschalten
                </button>
            </div>
        );
    } else if (lage === 'an_anderes_konto') {
        inhalt = (
            <div className="space-y-3">
                <p className="text-sm text-slate-700">Eingerichtet für ein anderes Konto: <strong>{stand?.email}</strong>.</p>
                <button type="button" onClick={() => void ausschalten()} disabled={beschaeftigt} className={`${KNOPF} border border-slate-300 text-slate-700 bg-white active:bg-slate-50`}>
                    Entfernen
                </button>
                <p className="text-xs text-slate-500">Oder für Ihr Konto einrichten (ersetzt den Eintrag):</p>
                {einrichtenFormular}
            </div>
        );
    } else {
        inhalt = einrichtenFormular;
    }

    return (
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-1">
                <Fingerprint className="w-4 h-4 text-sky-600" /> Anmelden mit Fingerabdruck / Gesicht
            </h2>
            <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                Gilt nur auf diesem Gerät. Das Passwort liegt verschlüsselt in der App; der Schlüssel dazu steckt im
                Schlüsselspeicher von Android und verlässt das Gerät nicht. Beim Anmelden geht das Passwort wie beim
                Eintippen verschlüsselt an den Server.
            </p>
            {inhalt}
            {fehler && <p className="mt-2 text-xs text-red-700">{fehler}</p>}
            {erfolg && <p className="mt-2 text-xs text-emerald-700">{erfolg}</p>}
        </section>
    );
}
